import React from "react";
import { compact } from "lodash";
import { useTranslation } from "react-i18next";
import { Redirect, RouteChildrenProps } from "react-router";
import { Route, Switch } from "react-router-dom";

import envelope from "../../../../commons/libs/externals/envelope";
import { getSpecConfig } from "../../../../commons/specs";
import { ActiveFilters, FilterGroups } from "../../../../commons/specs/filters";
import { ProductSpecKey, ProductType } from "../../../../commons/specs/product";
import { Brand } from "../../../../commons/types/brand";
import { GlobalLocationState } from "../../../../commons/types/location";
import { ProductFinderOverviewInitialTab } from "../../../../commons/types/settings";
import * as icons from "../../../../resources/icons";
import Button from "../../../commons/components/Button/Button";
import CurrentPageBreadcrumb from "../../../commons/components/CurrentPageBreadcrumb/CurrentPageBreadcrumb";
import Dot from "../../../commons/components/Dot/Dot";
import FlexLayout from "../../../commons/components/FlexLayout/FlexLayout";
import Icon from "../../../commons/components/Icon/Icon";
import IconLink from "../../../commons/components/IconLink/IconLink";
import MainContentLayout from "../../../commons/components/MainContentLayout/MainContentLayout";
import TabContent from "../../../commons/components/TabContent/TabContent";
import TabLink from "../../../commons/components/TabContent/TabLink";
import Tooltip, { HandleProps, TooltipPosition } from "../../../commons/components/Tooltip/Tooltip";
import { ROUTES } from "../../../commons/routes";
import { areNewAndUnconfiguredModelYearsInBrands } from "../../libs/brand";
import { isActiveFilter } from "../../libs/filters";
import { useAppHistory, useAppLocation } from "../../libs/hooks/use-app-routing";
import { useMediaQuery } from "../../libs/hooks/use-media-query";
import useSizingAvailability from "../../libs/hooks/use-sizing-availability";
import { useBrands } from "../../libs/queries/use-brands";
import useEnv from "../../libs/queries/use-env";
import useProductFilter from "../../libs/queries/use-product-filter";
import { useSettings } from "../../libs/queries/use-settings";
import { useCommonAppDispatcher } from "../../store/hooks";

export interface RouteDefinition {
  path: string;
  component: React.ComponentType<any>;
  title: string;
  value: ProductFinderOverviewInitialTab;
  props?: object;
}

interface Props {
  backButton: React.ReactNode;
  productFinderOverviewRoutes: RouteDefinition[];
  isOffline: boolean;
}

const tabHasBrandNotification = (route: RouteDefinition): boolean => {
  return route.value === ProductFinderOverviewInitialTab.Brands;
};

const tabHasActiveFilter = <Type extends ProductType>(
  productFilterGroups: FilterGroups<Type>,
  route: RouteDefinition,
  activeFilters: ActiveFilters<Type>
): boolean => {
  switch (route.value) {
    case ProductFinderOverviewInitialTab.Details:
      return productFilterGroups
        .flatMap(group =>
          group.filters.flatMap(filter =>
            Array.isArray(filter)
              ? filter.map(filterKey => isActiveFilter(activeFilters, filterKey))
              : isActiveFilter(activeFilters, filter)
          )
        )
        .some(isActive => isActive);

    case ProductFinderOverviewInitialTab.Categories:
      return !!isActiveFilter(activeFilters, ProductSpecKey.CategoryKey);

    default:
      return false;
  }
};

const ProductFinderOverviewPartial = ({ backButton, productFinderOverviewRoutes, isOffline }: Props) => {
  const [dispatch, actions] = useCommonAppDispatcher();
  const history = useAppHistory();
  const { t } = useTranslation(["commons"]);

  const env = useEnv();
  const settings = useSettings();
  const brands = useBrands();

  const specConfig = getSpecConfig(env.productType);

  const location = useAppLocation();
  const breakpoints = useMediaQuery();

  const productFilter = useProductFilter({
    productType: env.productType,
    assortmentFilterSettings: settings.assortmentFilterSettings
  });

  const sizingAvailability = useSizingAvailability({ isOnline: !isOffline });

  const currentRoutePath = location.pathname;
  const productFinderOverviewInitialTab = settings.initialized.customization.productFinderOverviewInitialTab;

  const handleLinkClick = (_event: React.MouseEvent<HTMLElement>, href: string) => {
    history.push(href);
  };

  const handleTabClick = (_event: React.MouseEvent<HTMLElement>, href: string) => {
    const locationState: GlobalLocationState = location?.state?.goBackToDetails
      ? {
          goBackToDetails: location.state.goBackToDetails
        }
      : {};

    history.replace(href, locationState);
  };

  const [sortedRoutes, initialProductFinderOverviewRoute] = React.useMemo(
    () => [
      compact([
        productFinderOverviewRoutes.find((route: RouteDefinition) => route.value === productFinderOverviewInitialTab),
        ...productFinderOverviewRoutes.filter(
          (route: RouteDefinition) => route.value !== productFinderOverviewInitialTab
        )
      ]),
      productFinderOverviewRoutes.find(route => route.value === productFinderOverviewInitialTab)?.path ??
        ROUTES.PRODUCT_FINDER_OVERVIEW.BRANDS
    ],
    [productFinderOverviewInitialTab, productFinderOverviewRoutes]
  );

  const renderUnconfigureBrandModelYearNotification = ({ close }: HandleProps) => (
    <FlexLayout direction="column" gap="xs">
      <strong>
        {settings.initialized.customization.modelYearDisplay.term === "modelYear"
          ? t("commons:productFinderOverviewPartialProductFinderOverview.newModelYearsAvailableTitle")
          : t("commons:productFinderOverviewPartialProductFinderOverview.newReleaseYearsAvailableTitle")}
      </strong>

      {envelope.isMobileBuild ? (
        <span>{t("commons:productFinderOverviewPartialProductFinderOverview.updateBrandsDevice")}</span>
      ) : (
        <Button
          size="s"
          variant="standout"
          onClick={() => {
            close();
            dispatch(
              actions.modal.showModal("auth", {
                onSuccess: () => dispatch(actions.modal.showModal("brandsModelYearActivation", {}))
              })
            );
          }}
        >
          {t("commons:productFinderOverviewPartialProductFinderOverview.updateButton")}
        </Button>
      )}
    </FlexLayout>
  );

  const renderTabLinkWithBrandActivationModal = (
    route: RouteDefinition,
    brandData: Brand[]
  ): React.ReactElement<React.ComponentProps<typeof TabLink>> => (
    <Route
      exact
      key={route.path}
      path={route.path}
      children={({ match }: RouteChildrenProps) => (
        <TabLink
          active={match !== null}
          label={route.title}
          href={route.path}
          onClick={handleTabClick}
          notification={
            tabHasBrandNotification(route) && areNewAndUnconfiguredModelYearsInBrands(brandData) ? (
              <Tooltip
                content={renderUnconfigureBrandModelYearNotification}
                isCloseable
                openOnMount
                openOnMountDelay={1000}
                shouldCloseAutomatically={false}
                preferredPosition={TooltipPosition.Bottom}
                closeOnOutsideClick={false}
                variant="accent"
                extraHorizontalPadding
              >
                <Dot pulse />
              </Tooltip>
            ) : tabHasActiveFilter(specConfig.filterGroups, route, productFilter.activeFilters) && !match ? (
              <Dot />
            ) : undefined
          }
        />
      )}
    />
  );

  const renderTabLink = (route: RouteDefinition): React.ReactElement<React.ComponentProps<typeof TabLink>> => {
    return (
      <Route
        exact
        key={route.path}
        path={route.path}
        children={({ match }: RouteChildrenProps) => (
          <TabLink
            active={match !== null}
            label={route.title}
            href={route.path}
            onClick={handleTabClick}
            notification={
              tabHasActiveFilter(specConfig.filterGroups, route, productFilter.activeFilters) && !match ? (
                <Dot />
              ) : undefined
            }
          />
        )}
      />
    );
  };

  const renderTabNavigationLinks = (
    sortedRoutes: RouteDefinition[],
    brands: Brand[]
  ): Array<React.ReactElement<React.ComponentProps<typeof TabLink>>> => {
    return sortedRoutes.map((route: RouteDefinition) => {
      if (tabHasBrandNotification(route)) {
        return renderTabLinkWithBrandActivationModal(route, brands);
      } else {
        return renderTabLink(route);
      }
    });
  };

  return (
    <MainContentLayout
      headerLeft={backButton}
      headerRight={
        <>
          <CurrentPageBreadcrumb>
            {t("commons:productFinderOverviewPartialProductFinderOverview.findBikes")}
          </CurrentPageBreadcrumb>
          {currentRoutePath === ROUTES.PRODUCT_FINDER_OVERVIEW.BRANDS && !envelope.isMobileBuild && (
            <IconLink
              kind="support"
              href={ROUTES.SETTINGS.BRAND_ACTIVATION}
              onClick={handleLinkClick}
              icon={<Icon source={icons.IconSmallSettings} />}
            >
              {t("commons:productFinderOverviewPartialProductFinderOverview.configureBrandsButton")}
            </IconLink>
          )}
        </>
      }
      noPaddingBottom={
        !breakpoints.xl &&
        (currentRoutePath === ROUTES.PRODUCT_FINDER_OVERVIEW.CATEGORIES ||
          currentRoutePath === ROUTES.PRODUCT_FINDER_OVERVIEW.DETAILS)
      }
    >
      <TabContent tabLinksAlignment="center" tabLinks={renderTabNavigationLinks(sortedRoutes, brands.active ?? [])}>
        <Switch>
          <Redirect exact from={ROUTES.PRODUCT_FINDER_OVERVIEW.INDEX} to={initialProductFinderOverviewRoute} />
          {productFinderOverviewRoutes.map(route => (
            <Route
              exact
              key={route.path}
              path={route.path}
              render={props =>
                React.createElement(route.component, {
                  ...props,
                  ...(route.props ?? {}),
                  sizingAvailability,
                  productFilter
                })
              }
            />
          ))}
        </Switch>
      </TabContent>
    </MainContentLayout>
  );
};

export default ProductFinderOverviewPartial;
